import { useSortable } from '@dnd-kit/sortable';
import { useTasksStore } from '@/stores/tasksStore';
import { api } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { CSS } from '@dnd-kit/utilities';
import { TaskUI } from '@flexiplanner/types';
import { Badge } from '@/components/ui/badge';
import { getEnergyColor, getPriorityColor, formatDuration, formatTime } from '@/lib/utils';
import { GripVertical, ChevronDown, ChevronRight, Clock, Zap, Play, Layers, FolderKanban } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { ActivitySessionLogger } from '@/components/activities/ActivitySessionLogger';
import { useState } from 'react';
import { formatMissingPlanningFields, getMissingPlanningFields } from '@/lib/activityReadiness';
import { useToast } from '@/hooks/use-toast';

interface TaskBlockProps {
  task: TaskUI;
  children?: TaskUI[];
  onEdit: (taskId: string) => void;
  onToggleExpand?: (taskId: string) => void;
  isExpanded?: boolean;
  depth?: number;
}

export function TaskBlock({
  task,
  children = [],
  onEdit,
  onToggleExpand,
  isExpanded = true,
  depth = 0,
}: TaskBlockProps) {
  const updateTask = useTasksStore(s => s.updateTask);
  const { toast } = useToast();
  const [isLoggingSession, setIsLoggingSession] = useState(false);
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const bundle = useWorkspaceStore(s => s.getBundle(task.bundleId));
  const project = useWorkspaceStore(s => s.getProject(bundle?.projectId || task.projectId));
  const missingPlanningFields = getMissingPlanningFields(task);
  const missingPlanningLabel = formatMissingPlanningFields(missingPlanningFields);
  const isPlanningComplete = missingPlanningFields.length === 0;
  const showPlanningRequiredToast = () => {
    toast({
      title: 'Fill planning details first',
      description: `Missing ${missingPlanningLabel}.`,
      variant: 'destructive',
    });
  };
  const handleComplete = (checked: boolean) => {
    if (checked) {
      if (!isPlanningComplete) {
        showPlanningRequiredToast();
        return;
      }
      startImmersionMode(task.id);
      return;
    }

    const reopenedCompletion = Math.min(99, Math.max(0, task.completionPercent ?? 99));
    updateTask(task.id, {
      status: reopenedCompletion > 0 ? 'in_progress' : 'todo',
      completionPercent: reopenedCompletion,
    });
    api.tasks.update(task.id, {
      status: reopenedCompletion > 0 ? 'in_progress' : 'todo',
      completion_percent: reopenedCompletion,
      completed_at: null,
    }).catch(err => {
      console.error('Failed to reopen activity', err);
      updateTask(task.id, { status: task.status, completionPercent: task.completionPercent });
    });
  };
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const hasChildren = children.length > 0;
  const energyColor = task.energyLevel ? getEnergyColor(task.energyLevel) : undefined;
  const priorityColor = getPriorityColor(task.priority);
  const scheduleLabel =
    task.scheduledStart && task.scheduledEnd
      ? `${formatTime(new Date(task.scheduledStart))} - ${formatTime(new Date(task.scheduledEnd))}`
      : null;
  const canLogActivitySession = Boolean(scheduleLabel);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group mb-2 rounded-md border border-border/80 bg-card/95 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:border-ring/50 hover:shadow-sm',
        isDragging && 'opacity-50',
        depth > 0 && 'ml-6'
      )}
    >
      <div className="flex items-start gap-2 p-3">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none rounded-md p-1 opacity-100 transition-opacity hover:bg-secondary sm:opacity-0 sm:group-hover:opacity-100"
          aria-label="Drag handle"
        >
          <GripVertical className="text-muted-foreground h-5 w-5" />
        </button>

        {/* Expand/collapse button */}
        {hasChildren && onToggleExpand && (
          <button
            onClick={() => onToggleExpand(task.id)}
            className="flex-shrink-0"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            )}
          </button>
        )}

        {/* Activity content */}
        <div className="pt-1" onPointerDown={e => e.stopPropagation()}>
          <Checkbox
            checked={task.status === 'done' || task.status === 'completed'}
            onCheckedChange={handleComplete}
            aria-label={`Log progress for ${task.title}`}
          />
        </div>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onEdit(task.id)}>
          <div className="flex items-start gap-2">
            <h4
              className={cn(
                'line-clamp-2 text-sm font-medium leading-snug',
                (task.status === 'done' || task.status === 'completed') &&
                  'text-muted-foreground line-through'
              )}
            >
              {task.title}
            </h4>
            <div className="flex flex-shrink-0 items-center gap-1">
              {/* Priority badge */}
              <Badge
                variant="outline"
                className="text-xs"
                style={{ borderColor: priorityColor, color: priorityColor }}
              >
                P{task.priority}
              </Badge>

              {/* Energy level */}
              {task.energyLevel && (
                <div
                  className="flex items-center gap-1 text-xs"
                  style={{ color: energyColor }}
                  title={`Energy: ${task.energyLevel}`}
                >
                  <Zap className="h-3 w-3" />
                  {task.energyLevel}
                </div>
              )}
            </div>

            {/* Immersion Mode Button */}
            <div className="ml-auto" onPointerDown={e => e.stopPropagation()}>
              {canLogActivitySession && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (!isPlanningComplete) {
                      showPlanningRequiredToast();
                      return;
                    }
                    setIsLoggingSession(true);
                  }}
                  disabled={!isPlanningComplete}
                  className="text-muted-foreground hover:text-primary hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 rounded-md p-1 transition-colors"
                  title={isPlanningComplete ? 'Log activity session' : `Missing ${missingPlanningLabel}`}
                  aria-label={`Log session for ${task.title}`}
                >
                  <Clock className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (!isPlanningComplete) {
                    showPlanningRequiredToast();
                    return;
                  }
                  startImmersionMode(task.id);
                }}
                disabled={!isPlanningComplete}
                className="text-muted-foreground hover:text-primary hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 rounded-md p-1 transition-colors"
                title={isPlanningComplete ? 'Start activity now' : `Missing ${missingPlanningLabel}`}
              >
                <Play className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{task.description}</p>
          )}

          {/* Metadata */}
          <div className="text-muted-foreground mt-2 grid gap-1.5 text-xs">
            {bundle && (
              <div className="flex min-w-0 items-center gap-1.5">
                <Layers className="h-3 w-3 shrink-0" style={{ color: bundle.color }} />
                <span className="truncate">{bundle.name}</span>
              </div>
            )}
            {project && (
              <div className="flex min-w-0 items-center gap-1.5">
                <FolderKanban className="h-3 w-3 shrink-0" style={{ color: project.color }} />
                <span className="truncate">{project.name}</span>
              </div>
            )}
            {task.estimatedDuration && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(task.estimatedDuration)}
              </div>
            )}
            {scheduleLabel && (
              <div className="flex items-center gap-1 font-medium text-foreground">
                <Clock className="h-3 w-3" />
                Scheduled {scheduleLabel}
              </div>
            )}
            {task.deadline && <span>Due: {new Date(task.deadline).toLocaleDateString()}</span>}
            {task.attributes && task.attributes.length > 0 && (
              <div className="flex gap-1">
                {task.attributes.map(attr => (
                  <Badge key={attr} variant="secondary" className="text-xs">
                    {attr}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nested children */}
      {hasChildren && isExpanded && (
        <div className="pb-2 pl-4">
          {children.map(child => (
            <TaskBlock
              key={child.id}
              task={child}
              onEdit={onEdit}
              onToggleExpand={onToggleExpand}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
      <ActivitySessionLogger
        task={task}
        open={isLoggingSession && canLogActivitySession}
        onClose={() => setIsLoggingSession(false)}
        onLogged={updatedTask => {
          updateTask(updatedTask.id, updatedTask);
          setIsLoggingSession(false);
        }}
      />
    </div>
  );
}
